import { useNavigate } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronRight } from "react-icons/fa";

import { useGetMyAccounts } from "@/api/queries";
import { useUIStateStore } from "@/store/uiStateStore";

import useGetColours from "../helpers/getColours";
import useAppStore from "../store/appStateZustand";
import ChangeAccountModal from "./ChangeAccountModal";
import ChooseAccountModal from "./ChooseAccountModal";
import LoadingSmall from "./LoadingSmall";
import RebrandButtonLink from "./RebrandButtonLink";
import Table from "./Table";

function ExpandCell() {
  return (
    <div className="float-right">
      <FaChevronRight />
    </div>
  );
}

function TopWriters({ data, isLoading, isError }) {
  const { t } = useTranslation("dashboard");
  const flags = useFlags();
  const disableTopGigs = flags["pd-disable-top-gigs"];

  const myAccounts = useGetMyAccounts();

  const { highlightColour } = useGetColours();
  const [switchModal, setSwitchModal] = useState(null);
  const [accountChoiceModal, setAccountChoiceModal] = useState(false);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const navigate = useNavigate();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const columns = React.useMemo(() => [
    {
      Header: t("dashboard:ra-column-name", "Writer"),
      accessor: row =>
        `${row?.firstname} ${row?.middlename ? row?.middlename : ""} ${
          row?.lastname
        }`,
      width: "25%",
      id: "colTopWriterName",
    },
    {
      Header: t("dashboard:ra-column-artist-account", "Account"),
      accessor: "accountname",
      width: "25%",
      id: "colTopWriterAccount",
    },
    {
      Header: "",
      accessor: "expander",
      width: "5%",
      Cell: ExpandCell,
    },
  ], [t]);

  const onRowClick = (row) => {
    const song = row.original;
    if (song.artistaccountcode === selectedAccount) {
      navigate({ to: "/writers" });
      return;
    }
    // switch account, and go to writers
    setSwitchModal({
      artistAccountCode: song.artistaccountcode,
      catalogueCode: song.cataloguecode,
      name: song.accountname,
    });
  };

  return (
    <>
      {!!switchModal && (
        <ChangeAccountModal
          accounts={myAccounts?.data?.artistaccounts}
          to="/writers"
          setSwitchModal={setSwitchModal}
          account={switchModal}
        />
      )}
      {!!accountChoiceModal && (
        <ChooseAccountModal
          open={accountChoiceModal}
          title={t("dashboard:choose-account-writer-modal-header", "Choose account")}
          to="/writers/create"
          setAccountChoiceModal={setAccountChoiceModal}
          accounts={myAccounts?.data?.artistaccounts}
          nonAdminTo="/writers/"
        />
      )}
      <div
        className={`${
          isTunecore
            ? "bg-tunecore-dark-element text-white w-full"
            : "bg-white text-black w-full lg:w-1/2"
        } ${disableTopGigs ? "lg:grow" : ""} p-8 rounded-lg`}
      >
        <div className="flex flex-row items-center justify-between mb-5">
          <h2 className="text-xl font-medium">
            {t("dashboard:recently-added-writers-header", "Recently added writers")}
          </h2>
          <RebrandButtonLink
            plusIcon
            onClick={() => {
              setAccountChoiceModal(true);
            }}
            id="btnTopWriterAddWriter"
          >
            {t("dashboard:add-new-writer-link", "Add new writer")}
          </RebrandButtonLink>
        </div>
        {isLoading && <LoadingSmall />}
        {!isLoading && !isError && data && (
          <div>
            <Table
              area="TopWriters"
              data={data}
              columns={columns}
              min={5}
              highlightColour={highlightColour}
              revealComponent={null}
              onRowClick={onRowClick}
              searchSuffix={t("dashboard:writers-search-suffix", "Search Writers")}
              disableSearch
              bgClass={`${
                isTunecore ? "bg-tunecore-dark-element" : "bg-white"
              } rounded-lg p-0`}
              initialSort={[
                {
                  id: "verifieddate",
                  desc: true,
                },
              ]}
            />
          </div>
        )}
        {isError && (
          <div>
            <p>{t("dashboard:recent-writers-error", "Error with recent writers")}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default TopWriters;
